<script setup>
import { computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  open: { type: Boolean, default: false },
  alerts: { type: Array, default: () => [] },
  unreadCount: { type: Number, default: 0 },
});
const emit = defineEmits(['close', 'acknowledge', 'acknowledgeAll']);

const LEVEL_STYLES = {
  crise: { label: 'Crise', bg: 'bg-rose-600', text: 'text-white', icon: 'i-lucide-flame' },
  alto: { label: 'Alto', bg: 'bg-rose-100', text: 'text-rose-800', icon: 'i-lucide-triangle-alert' },
  medio: { label: 'Médio', bg: 'bg-amber-100', text: 'text-amber-800', icon: 'i-lucide-alert-circle' },
  baixo: { label: 'Baixo', bg: 'bg-slate-100', text: 'text-slate-700', icon: 'i-lucide-info' },
};

const TYPE_LABEL = {
  spike: 'Pico de volume',
  sentiment_drop: 'Queda de sentimento',
  emerging_topic: 'Tema emergente',
  high_priority_mention: 'Menção crítica',
  coordinated_attack: 'Ataque coordenado',
};

const grouped = computed(() => {
  const order = ['crise', 'alto', 'medio', 'baixo'];
  const groups = {};
  props.alerts.forEach(a => {
    const k = a.level || 'baixo';
    groups[k] ||= [];
    groups[k].push(a);
  });
  return order.filter(k => groups[k]?.length).map(k => ({ level: k, items: groups[k] }));
});

const timeAgo = iso => {
  if (!iso) return '';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return 'agora';
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)} h`;
  return `há ${Math.floor(diff / 86400)} d`;
};
</script>

<template>
  <Teleport to="body">
    <transition name="slide">
      <div v-if="open" class="fixed inset-0 z-50 flex" @click.self="emit('close')">
        <div class="flex-1 bg-black/40" @click="emit('close')" />
        <aside class="h-full w-full max-w-md overflow-y-auto border-l border-n-weak bg-n-background shadow-2xl">
          <div class="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-n-weak bg-n-background p-4">
            <div>
              <h2 class="text-sm font-bold text-n-slate-12">Alertas</h2>
              <p class="text-[11px] text-n-slate-11">
                {{ unreadCount }} não lidos
              </p>
            </div>
            <div class="flex gap-2">
              <Button
                v-if="unreadCount > 0"
                variant="outline"
                size="sm"
                icon="i-lucide-check-check"
                @click="emit('acknowledgeAll')"
              >
                Marcar todos
              </Button>
              <Button variant="ghost" size="sm" icon="i-lucide-x" @click="emit('close')" />
            </div>
          </div>

          <div class="p-4">
            <div v-if="!alerts.length" class="py-10 text-center text-sm text-n-slate-11">
              Nenhum alerta no momento. Tudo tranquilo. 👌
            </div>

            <div v-for="group in grouped" :key="group.level" class="mb-5">
              <div
                class="mb-2 inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[11px] font-bold"
                :class="[LEVEL_STYLES[group.level]?.bg, LEVEL_STYLES[group.level]?.text]"
              >
                <span :class="LEVEL_STYLES[group.level]?.icon" class="size-3" />
                {{ LEVEL_STYLES[group.level]?.label }} · {{ group.items.length }}
              </div>

              <div class="space-y-2">
                <div
                  v-for="a in group.items"
                  :key="a.id"
                  class="rounded-lg border border-n-weak p-3"
                  :class="a.acknowledgedAt ? 'opacity-60' : ''"
                >
                  <div class="mb-1 flex items-start justify-between gap-2">
                    <div class="min-w-0 flex-1">
                      <p class="text-[10px] uppercase tracking-wide text-n-slate-11">
                        {{ TYPE_LABEL[a.alertType] || a.alertType }}
                      </p>
                      <p class="text-sm font-semibold text-n-slate-12">
                        {{ a.title }}
                      </p>
                    </div>
                    <span class="shrink-0 text-[10px] text-n-slate-11">
                      {{ timeAgo(a.createdAt) }}
                    </span>
                  </div>
                  <p class="mb-2 text-xs text-n-slate-11">{{ a.body }}</p>
                  <div v-if="!a.acknowledgedAt" class="flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      icon="i-lucide-check"
                      @click="emit('acknowledge', a.id)"
                    >
                      Marcar como lido
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: opacity 0.2s ease;
}
.slide-enter-from,
.slide-leave-to {
  opacity: 0;
}
</style>
